/**
 * Create Student Page
 * Form to create a new student
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useNotification } from '@/hooks/useNotification';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Modal } from '@/components/common/Modal';
import { apiRequest } from '@/api/client';
import { sanitizeStudentData } from '@/utils/inputSanitizer';
import { getUserFriendlyError } from '@/utils/errorMessages';

interface StudentFormData {
  firstName: string;
  lastName: string;
  classId: string;
  parentEmail: string;
}

interface Class {
  classId: string;
  className: string;
  gradeLevel: string;
}

interface CreatedStudent {
  username: string;
  password: string;
  firstName: string;
  lastName: string;
}

export const CreateStudentPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [classes, setClasses] = useState<Class[]>([]);
  const [formData, setFormData] = useState<StudentFormData>({
    firstName: '',
    lastName: '',
    classId: '',
    parentEmail: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof StudentFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdStudent, setCreatedStudent] = useState<CreatedStudent | null>(null);
  const [showCredentials, setShowCredentials] = useState(false);

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      // Fetch classes based on user role
      const action = user?.isAdmin ? 'get_school_classes' : 'get_teacher_classes';
      const params = user?.isAdmin 
        ? { schoolId: user?.schoolId }
        : { userEmail: user?.email };
      
      const response = await apiRequest<{ classes: Class[] }>(action, params);

      if (response.success && (response as any).classes) {
        setClasses((response as any).classes);
      }
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    }
  };

  const handleChange = (field: keyof StudentFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof StudentFormData, string>> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.classId) {
      newErrors.classId = 'Class is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Sanitize form data before submission
      const sanitized = sanitizeStudentData({
        firstName: formData.firstName,
        lastName: formData.lastName,
        classId: formData.classId,
        parentEmail: formData.parentEmail,
        username: '', // Will be generated by backend
        password: '', // Will be generated by backend
        ageRating: 10 // Default, will be set by backend based on class
      });

      // Find the selected class to get schoolId
      const selectedClass = classes.find(c => c.classId === sanitized.classId);
      
      const response = await apiRequest('create_student', {
        teacherEmail: user?.email,
        firstName: sanitized.firstName,
        lastName: sanitized.lastName,
        classId: sanitized.classId,
        schoolId: user?.schoolId || (selectedClass as any)?.schoolId,
        yearGroup: (selectedClass as any)?.gradeLevel || '',
        parentEmail: sanitized.parentEmail || undefined,
      });

      if (response.success && (response as any).student) {
        const student = (response as any).student;
        setCreatedStudent({
          username: student.username,
          password: student.password,
          firstName: student.firstName,
          lastName: student.lastName,
        });
        setShowCredentials(true);
        showNotification('success', 'Student created successfully!');
      } else {
        showNotification('error', getUserFriendlyError(response, 'Student'));
      }
    } catch (error) {
      showNotification('error', getUserFriendlyError(error, 'Student'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyCredentials = () => {
    if (createdStudent) {
      const text = `Username: ${createdStudent.username}\nPassword: ${createdStudent.password}`;
      navigator.clipboard.writeText(text);
      showNotification('success', 'Credentials copied to clipboard!');
    }
  };

  const handleDone = () => {
    navigate('/students');
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Add New Student</h1>
        <p className="text-slate-600 mt-2">Create a student account and generate login credentials</p>
      </div>

      {/* Form */}
      <Card>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <Input
            label="First Name"
            type="text"
            value={formData.firstName}
            onChange={(e) => handleChange('firstName', e.target.value)}
            error={errors.firstName}
            placeholder="John"
            required
          />

          <Input
            label="Last Name"
            type="text"
            value={formData.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            error={errors.lastName}
            placeholder="Smith"
            required
          />

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Class <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.classId}
              onChange={(e) => handleChange('classId', e.target.value)}
              className={`w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                errors.classId ? 'border-red-500' : 'border-slate-300'
              }`}
              required
            >
              <option value="">Select a class</option>
              {classes.map((cls) => (
                <option key={cls.classId} value={cls.classId}>
                  {cls.className} ({cls.gradeLevel})
                </option>
              ))}
            </select>
            {errors.classId && (
              <p className="mt-1 text-sm text-red-600">{errors.classId}</p>
            )}
          </div>

          <Input
            label="Parent Email (Optional)"
            type="email"
            value={formData.parentEmail}
            onChange={(e) => handleChange('parentEmail', e.target.value)}
            error={errors.parentEmail}
            placeholder="parent@example.com"
          />

          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/students')}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={isSubmitting}
              className="flex-1"
            >
              Create Student
            </Button>
          </div>
        </form>
      </Card>

      {/* Credentials Modal */}
      <Modal
        isOpen={showCredentials}
        onClose={handleDone}
        title="Student Created Successfully!"
      >
        <div className="space-y-4">
          <p className="text-slate-600">
            Login credentials for <strong>{createdStudent?.firstName} {createdStudent?.lastName}</strong>:
          </p>

          <div className="bg-slate-50 rounded-xl p-4 space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-600">Username</label>
              <p className="text-lg font-mono font-bold text-slate-900">{createdStudent?.username}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600">Password</label>
              <p className="text-lg font-mono font-bold text-slate-900">{createdStudent?.password}</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm text-blue-800">
              💡 <strong>Tip:</strong> You can download formatted credentials from the student's detail page or download all class credentials from the class page.
            </p>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleCopyCredentials}
              className="flex-1"
            >
              📋 Copy Credentials
            </Button>
            <Button
              variant="primary"
              onClick={handleDone}
              className="flex-1"
            >
              Done
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
